// ===== Toolbox (bottom, tabbed): Trade, Exposure, History, News, Mailbox, Calendar, Alerts, Articles, Code Base, Experts, Journal, Tasks =====
import { useState } from "react";
import { useStore, setUI, update, describeAlert, posPrice, posProfit, closePosition, cancelOrder, accountInfo, fmtTime, fmtMoney, getState } from "./store";
import { openMenu } from "./ContextMenu";
import { I } from "./Icons";

const TABS = ["Trade", "Exposure", "History", "News", "Mailbox", "Calendar", "Alerts", "Articles", "Code Base", "Experts", "Journal", "Tasks"];
const NEWS = [
  ["Market Wrap", "Dollar steadies ahead of central bank minutes; euro holds near weekly high"],
  ["Commodities", "Gold extends gains as real yields slip for a third session"],
  ["Crypto", "Bitcoin volatility compresses to multi-month lows before options expiry"],
  ["Forex", "Yen weakens past key level as policy divergence widens"],
  ["Equities", "Tech-heavy index futures edge higher on strong earnings guidance"],
  ["Energy", "Crude inventories fall more than expected, oil climbs"],
];
const CAL = [
  ["USD", "Non-Farm Payrolls", 3, "185K", "175K"], ["EUR", "ECB Interest Rate Decision", 3, "4.25%", "4.25%"], ["GBP", "CPI y/y", 3, "3.1%", "3.2%"],
  ["JPY", "BoJ Press Conference", 2, "", ""], ["USD", "Initial Jobless Claims", 2, "221K", "218K"], ["AUD", "Employment Change", 2, "22.5K", "30.0K"],
  ["CAD", "GDP m/m", 1, "0.2%", "0.3%"], ["EUR", "German ZEW Economic Sentiment", 2, "47.1", "50.0"],
];
const ARTICLES = ["Building a custom trailing stop module", "Statistical arbitrage on currency pairs", "Neural networks for price pattern recognition", "Optimizing position sizing with Kelly criterion", "Working with tick data in custom indicators"];

const Pl = ({ v }) => <span className={v >= 0 ? "pos" : "neg"}>{fmtMoney(v)}</span>;

const TradeTab = ({ st }) => {
  const a = accountInfo();
  const sym = (n) => st.symbols[n];
  const rowMenu = (e, p, isOrder) => openMenu(e, [
    { label: "New Order", shortcut: "F9", onClick: () => setUI({ dialog: { kind: "order", symbol: p.symbol, mode: "market" } }) },
    isOrder ? { label: "Delete Order", onClick: () => cancelOrder(p.ticket) } : { label: "Close Position", onClick: () => closePosition(p.ticket) },
    { label: isOrder ? "Modify Order" : "Modify or Delete", onClick: () => setUI({ dialog: { kind: "modify", ticket: p.ticket } }) },
    { sep: true },
    { label: "Close All Positions", disabled: !st.positions.length, onClick: () => st.positions.map((x) => x.ticket).forEach((t) => closePosition(t)) },
    { label: "Close Profitable Positions", onClick: () => st.positions.filter((x) => posProfit(x) > 0).map((x) => x.ticket).forEach((t) => closePosition(t)) },
    { label: "Close Losing Positions", onClick: () => st.positions.filter((x) => posProfit(x) < 0).map((x) => x.ticket).forEach((t) => closePosition(t)) },
  ]);
  return (
    <table className="mt-table tb-table" data-testid="trade-table">
      <thead><tr><th className="l">Symbol</th><th>Ticket</th><th>Time</th><th>Type</th><th>Volume</th><th>Price</th><th>S / L</th><th>T / P</th><th>Price</th><th>Swap</th><th>Profit</th><th className="x"></th></tr></thead>
      <tbody onContextMenu={(e) => e.target === e.currentTarget && rowMenu(e, { symbol: "EURUSD" })}>
        {st.positions.map((p) => {
          const s = sym(p.symbol), pr = posProfit(p);
          return (
            <tr key={p.ticket} data-testid={`position-row-${p.ticket}`} onDoubleClick={() => setUI({ dialog: { kind: "modify", ticket: p.ticket } })} onContextMenu={(e) => rowMenu(e, p)}>
              <td className="l"><span className={`ty-ico ${p.type}`} />{p.symbol}</td><td>{p.ticket}</td><td>{fmtTime(p.time, true)}</td><td>{p.type}</td>
              <td>{p.volume.toFixed(2)}</td><td>{p.price.toFixed(s.digits)}</td><td>{p.sl ? p.sl.toFixed(s.digits) : ""}</td><td>{p.tp ? p.tp.toFixed(s.digits) : ""}</td>
              <td data-testid={`position-price-${p.ticket}`}>{posPrice(p).toFixed(s.digits)}</td><td>{p.swap.toFixed(2)}</td>
              <td data-testid={`position-profit-${p.ticket}`}><Pl v={pr} /></td>
              <td className="x"><button className="row-x" title="Close position" data-testid={`position-close-${p.ticket}`} onClick={() => closePosition(p.ticket)}><I.close /></button></td>
            </tr>
          );
        })}
        <tr className="acc-row" data-testid="account-summary">
          <td className="l" colSpan={10}>
            <span className="acc-ico" /> Balance: <b data-testid="acc-balance">{fmtMoney(a.balance)}</b> USD&nbsp;&nbsp; Equity: <b data-testid="acc-equity">{fmtMoney(a.equity)}</b>&nbsp;&nbsp; Margin: <b>{fmtMoney(a.margin)}</b>&nbsp;&nbsp; Free Margin: <b>{fmtMoney(a.free)}</b>&nbsp;&nbsp; {a.margin ? <>Margin Level: <b>{fmtMoney(a.level)}</b>%</> : null}
          </td>
          <td data-testid="acc-profit"><Pl v={a.profit} /></td><td />
        </tr>
        {st.orders.map((o) => {
          const s = sym(o.symbol);
          return (
            <tr key={o.ticket} data-testid={`order-row-${o.ticket}`} onDoubleClick={() => setUI({ dialog: { kind: "modify", ticket: o.ticket } })} onContextMenu={(e) => rowMenu(e, o, true)}>
              <td className="l"><span className={`ty-ico ${o.type.startsWith("buy") ? "buy" : "sell"}`} />{o.symbol}</td><td>{o.ticket}</td><td>{fmtTime(o.time, true)}</td><td>{o.type.replace("_", " ")}</td>
              <td>{o.volume.toFixed(2)} / 0.00</td><td>{o.price.toFixed(s.digits)}</td><td>{o.sl ? o.sl.toFixed(s.digits) : ""}</td><td>{o.tp ? o.tp.toFixed(s.digits) : ""}</td>
              <td>{(o.type.startsWith("buy") ? s.ask : s.bid).toFixed(s.digits)}</td><td></td><td className="sec">placed</td>
              <td className="x"><button className="row-x" title="Delete order" data-testid={`order-cancel-${o.ticket}`} onClick={() => cancelOrder(o.ticket)}><I.close /></button></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const ExposureTab = ({ st }) => {
  const agg = {};
  st.positions.forEach((p) => {
    const sign = p.type === "buy" ? 1 : -1;
    const s = st.symbols[p.symbol];
    const base = p.symbol.slice(0, 3), quote = p.symbol.length === 6 ? p.symbol.slice(3) : "USD";
    agg[base] = (agg[base] || 0) + sign * p.volume * s.contract;
    agg[quote] = (agg[quote] || 0) - sign * p.volume * s.contract * p.price;
  });
  return (
    <table className="mt-table tb-table" data-testid="exposure-table">
      <thead><tr><th className="l">Asset</th><th>Volume</th><th>Rate</th><th>USD</th><th className="l">Graph</th></tr></thead>
      <tbody>
        {Object.entries(agg).map(([k, v]) => (
          <tr key={k}><td className="l">{k}</td><td className={v >= 0 ? "pos" : "neg"}>{fmtMoney(v)}</td><td>1.00</td><td>{fmtMoney(v)}</td><td className="l"><div className="exp-bar" style={{ width: Math.min(200, Math.abs(v) / 2000), background: v >= 0 ? "#1E88E5" : "#E53935" }} /></td></tr>
        ))}
        {!Object.keys(agg).length && <tr><td className="l sec" colSpan={5}>No open positions</td></tr>}
      </tbody>
    </table>
  );
};

const HistoryTab = ({ st }) => {
  const total = st.history.reduce((a, h) => a + h.profit, 0);
  return (
    <table className="mt-table tb-table" data-testid="history-table">
      <thead><tr><th className="l">Time</th><th>Position</th><th>Symbol</th><th>Type</th><th>Volume</th><th>Price</th><th>S / L</th><th>T / P</th><th>Time</th><th>Price</th><th>Swap</th><th>Profit</th></tr></thead>
      <tbody>
        {st.history.map((h) => {
          const d = st.symbols[h.symbol].digits;
          return (
            <tr key={h.ticket + "-" + h.closeTime} data-testid={`history-row-${h.ticket}`}>
              <td className="l">{fmtTime(h.time, true)}</td><td>{h.ticket}</td><td>{h.symbol}</td><td>{h.type}</td><td>{h.volume.toFixed(2)}</td><td>{h.price.toFixed(d)}</td>
              <td>{h.sl ? h.sl.toFixed(d) : ""}</td><td>{h.tp ? h.tp.toFixed(d) : ""}</td><td>{fmtTime(h.closeTime, true)}</td><td>{h.closePrice.toFixed(d)}</td><td>{h.swap.toFixed(2)}</td><td><Pl v={h.profit} /></td>
            </tr>
          );
        })}
        <tr className="acc-row"><td className="l" colSpan={11}>Profit: <b><Pl v={total} /></b>&nbsp;&nbsp; Deals: {st.history.length}&nbsp;&nbsp; Balance: <b>{fmtMoney(st.account.balance)}</b></td><td><Pl v={total} /></td></tr>
      </tbody>
    </table>
  );
};

const LogTab = ({ rows, testId }) => (
  <table className="mt-table tb-table mono" data-testid={testId}>
    <thead><tr><th className="l" style={{ width: 160 }}>Time</th><th className="l" style={{ width: 110 }}>Source</th><th className="l">Message</th></tr></thead>
    <tbody>{rows.map((r) => <tr key={r.id}><td className="l">{fmtTime(r.time, true)}.{String(r.time % 1000).padStart(3, "0")}</td><td className="l">{r.source}</td><td className="l">{r.message}</td></tr>)}</tbody>
  </table>
);

const AlertsTab = ({ st }) => {
  const menu = (e, a) => openMenu(e, [
    { label: "Create", shortcut: "Insert", onClick: () => setUI({ dialog: { kind: "alert", symbol: a ? a.symbol : "EURUSD" } }) },
    { label: "Modify", disabled: !a, onClick: () => setUI({ dialog: { kind: "alert", id: a.id } }) },
    { label: a && a.enabled ? "Disable" : "Enable", disabled: !a, onClick: () => update(() => { a.enabled = !a.enabled; a.counter = 0; a.lastHit = false; }) },
    { label: "Delete", shortcut: "Delete", disabled: !a, onClick: () => update((x) => { x.alerts = x.alerts.filter((y) => y.id !== a.id); }) },
    { sep: true },
    { label: "Delete All", onClick: () => update((x) => { x.alerts = []; }) },
  ]);
  return (
    <table className="mt-table tb-table" data-testid="alerts-table" onContextMenu={(e) => menu(e, null)}>
      <thead><tr><th className="l">Symbol</th><th className="l">Condition</th><th>Counter</th><th>Limit</th><th className="l">Action</th><th className="l">Comment</th></tr></thead>
      <tbody>
        {st.alerts.map((a) => (
          <tr key={a.id} data-testid={`alert-row-${a.id}`} className={a.enabled ? "" : "sec"} onDoubleClick={() => setUI({ dialog: { kind: "alert", id: a.id } })} onContextMenu={(e) => menu(e, a)}>
            <td className="l"><span className={`al-ico ${a.enabled ? "on" : ""}`} />{a.symbol}</td><td className="l" data-testid={`alert-cond-${a.id}`}>{describeAlert(a)}</td><td data-testid={`alert-counter-${a.id}`}>{a.counter || 0}</td><td>{a.limit}</td><td className="l">{a.action}</td><td className="l">{a.comment}</td>
          </tr>
        ))}
        {!st.alerts.length && <tr><td className="l sec" colSpan={6}>Right-click → Create (or chart right-click → Create Alert) to add an alert</td></tr>}
      </tbody>
    </table>
  );
};

export default function Toolbox() {
  const st = useStore();
  const [tab, setTab] = useState("Trade");
  const now = Date.now();
  const content = {
    Trade: <TradeTab st={st} />,
    Exposure: <ExposureTab st={st} />,
    History: <HistoryTab st={st} />,
    News: <table className="mt-table tb-table" data-testid="news-table"><thead><tr><th className="l">Time</th><th className="l">Category</th><th className="l">Subject</th></tr></thead><tbody>{NEWS.map((n, i) => <tr key={i}><td className="l">{fmtTime(now - i * 1.7e6)}</td><td className="l">{n[0]}</td><td className="l">{n[1]}</td></tr>)}</tbody></table>,
    Mailbox: <table className="mt-table tb-table"><thead><tr><th className="l">From</th><th className="l">Subject</th><th className="l">Time</th></tr></thead><tbody><tr><td className="l">Web Terminal</td><td className="l">Welcome to the trading platform</td><td className="l">{fmtTime(now - 864e5)}</td></tr><tr><td className="l">WebTrade Ltd.</td><td className="l">Your demo account has been opened</td><td className="l">{fmtTime(now - 865e5)}</td></tr></tbody></table>,
    Calendar: <table className="mt-table tb-table" data-testid="calendar-table"><thead><tr><th className="l">Time</th><th className="l">Currency</th><th className="l">Event</th><th className="l">Importance</th><th>Forecast</th><th>Previous</th></tr></thead><tbody>{CAL.map((c, i) => <tr key={i}><td className="l">{fmtTime(now + (i - 2) * 54e5)}</td><td className="l">{c[0]}</td><td className="l">{c[1]}</td><td className="l"><span className={`imp imp-${c[2]}`}>{["", "low", "medium", "high"][c[2]]}</span></td><td>{c[3]}</td><td>{c[4]}</td></tr>)}</tbody></table>,
    Alerts: <AlertsTab st={st} />,
    Articles: <table className="mt-table tb-table"><thead><tr><th className="l">Date</th><th className="l">Title</th></tr></thead><tbody>{ARTICLES.map((a, i) => <tr key={i}><td className="l">{fmtTime(now - i * 3e8).slice(0, 10)}</td><td className="l">{a}</td></tr>)}</tbody></table>,
    "Code Base": <table className="mt-table tb-table"><thead><tr><th className="l">Name</th><th className="l">Type</th><th className="l">Description</th><th>Date</th></tr></thead><tbody>{[["Pivot Points", "Indicator", "Classic daily pivot levels"], ["Grid Hedger", "Expert", "Grid strategy with hedge"], ["Spread Logger", "Script", "Logs spread to file"]].map((r, i) => <tr key={i}><td className="l">{r[0]}</td><td className="l">{r[1]}</td><td className="l">{r[2]}</td><td>{fmtTime(now - i * 5e8).slice(0, 10)}</td></tr>)}</tbody></table>,
    Experts: <LogTab rows={st.experts} testId="experts-log" />,
    Journal: <LogTab rows={st.journal} testId="journal-log" />,
    Tasks: <table className="mt-table tb-table"><thead><tr><th className="l">Name</th><th className="l">Status</th><th>Progress</th></tr></thead><tbody><tr><td className="l sec" colSpan={3}>No tasks</td></tr></tbody></table>,
  };
  const badge = { Trade: st.positions.length + st.orders.length || "", Alerts: st.alerts.length || "", Mailbox: 2, News: NEWS.length };
  return (
    <div className="mt-panel toolbox" data-testid="toolbox">
      <div className="tbx-side"><button className="mt-panel-close" data-testid="toolbox-close" onClick={() => setUI({ panels: { ...getState().panels, toolbox: false } })}><I.close /></button><span className="tbx-vert">Toolbox</span></div>
      <div className="tbx-main">
        <div className="mt-scroll tbx-content">{content[tab]}</div>
        <div className="tbx-tabs" data-testid="toolbox-tabs">
          {TABS.map((t) => (
            <div key={t} className={`mt-tab ${tab === t ? "active" : ""}`} data-testid={`toolbox-tab-${t.toLowerCase().replace(" ", "-")}`} onClick={() => setTab(t)}>
              {t}{badge[t] ? <span className="tab-badge">{badge[t]}</span> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
